// src/components/common/FilePreviewModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Download, ExternalLink, FileText, File, Film, Music, 
  Image as ImageIcon, ZoomIn, ZoomOut, RotateCw, Maximize2, 
  Search, Copy, Check, Play, Pause, Volume2, VolumeX, 
  AlertCircle, Loader2, RefreshCw, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { publicAPI, getFileCategory } from '../../services/api';
import * as docx from 'docx-preview';
import * as XLSX from 'xlsx';

const FilePreviewModal = ({ material, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('other');
  
  // Image state
  const [imageScale, setImageScale] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);

  // Text/Code state
  const [textContent, setTextContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [textSearch, setTextSearch] = useState('');

  // Spreadsheet state
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [sheetHtml, setSheetHtml] = useState('');
  const [workbookRef, setWorkbookRef] = useState(null);

  // Office viewer toggle (for PPT / DOC fallback)
  const [officeViewerType, setOfficeViewerType] = useState('google'); // 'google' | 'office'

  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioVolume, setAudioVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const docxContainerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !material) {
      setLoading(true);
      setError(null);
      setTextContent('');
      setSheetNames([]);
      setSheetHtml('');
      setWorkbookRef(null);
      setImageScale(1);
      setImageRotation(0);
      setIsPlaying(false);
      return;
    }

    const cat = publicAPI.getFileCategory(material);
    setCategory(cat);
    setLoading(true);
    setError(null);

    const previewUrl = publicAPI.getPreviewUrl(material._id);

    // Track view / download metric
    publicAPI.trackDownload(material._id).catch(() => {});

    // DOCX Rendering
    if (cat === 'docx') {
      const format = (material.file?.format || '').toLowerCase();
      if (format === 'doc') {
        // Old binary doc cannot be parsed by docx-preview directly; use embed fallback
        setLoading(false);
      } else {
        fetch(previewUrl)
          .then(res => {
            if (!res.ok) throw new Error('Failed to load document');
            return res.arrayBuffer();
          })
          .then(buffer => {
            if (docxContainerRef.current) {
              docxContainerRef.current.innerHTML = '';
              return docx.renderAsync(buffer, docxContainerRef.current, null, {
                className: 'docx-preview-container',
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false
              });
            }
          })
          .then(() => {
            setLoading(false);
          })
          .catch(err => {
            console.error('Docx preview error:', err);
            setError('Could not render DOCX locally. You can preview with Google Docs Viewer or download the file.');
            setLoading(false);
          });
      }
    } 
    // Spreadsheet Rendering
    else if (cat === 'spreadsheet') {
      fetch(previewUrl)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load spreadsheet');
          return res.arrayBuffer();
        })
        .then(buffer => {
          const workbook = XLSX.read(buffer, { type: 'array' });
          setWorkbookRef(workbook);
          setSheetNames(workbook.SheetNames || []);
          if (workbook.SheetNames?.length > 0) {
            const firstSheet = workbook.SheetNames[0];
            setActiveSheet(firstSheet);
            const html = XLSX.utils.sheet_to_html(workbook.Sheets[firstSheet], {
              header: '',
              footer: ''
            });
            setSheetHtml(html);
          }
          setLoading(false);
        })
        .catch(err => {
          console.error('Spreadsheet preview error:', err);
          setError('Could not render spreadsheet. You can download the file to view.');
          setLoading(false);
        });
    }
    // Text / Code Rendering
    else if (cat === 'code') {
      fetch(previewUrl)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load text content');
          return res.text();
        })
        .then(text => {
          setTextContent(text);
          setLoading(false);
        })
        .catch(err => {
          console.error('Text preview error:', err);
          setError('Could not load text content.');
          setLoading(false);
        });
    }
    // Image, Video, Audio, PDF, PPTX loading completes on DOM mount
    else {
      setLoading(false);
    }
  }, [isOpen, material]);

  // Handle Sheet tab switch
  const handleSheetSwitch = (sheetName) => {
    setActiveSheet(sheetName);
    if (workbookRef && workbookRef.Sheets[sheetName]) {
      const html = XLSX.utils.sheet_to_html(workbookRef.Sheets[sheetName]);
      setSheetHtml(html);
    }
  };

  // Keyboard shortcut for closing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !material) return null;

  const previewUrl = publicAPI.getPreviewUrl(material._id);
  const downloadUrl = publicAPI.getDownloadUrl(material._id);
  const fileName = material.file?.originalName || `${material.title}.${material.file?.format || 'file'}`;
  const fileFormat = (material.file?.format || 'FILE').toUpperCase();

  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`;
  const officeViewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(previewUrl)}`;

  const handleCopyText = () => {
    navigator.clipboard.writeText(textContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const getCategoryBadge = () => {
    switch (category) {
      case 'image':
        return { label: 'Image', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: ImageIcon };
      case 'video':
        return { label: 'Video Player', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20', icon: Film };
      case 'audio':
        return { label: 'Audio Track', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: Music };
      case 'pdf':
        return { label: 'PDF Document', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: FileText };
      case 'docx':
        return { label: 'Word Document', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: FileText };
      case 'pptx':
        return { label: 'PowerPoint Presentation', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: PresentationIcon };
      case 'spreadsheet':
        return { label: 'Excel Spreadsheet', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: FileText };
      case 'code':
        return { label: 'Source Code / Text', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', icon: FileText };
      default:
        return { label: 'File Preview', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20', icon: File };
    }
  };

  const badge = getCategoryBadge();
  const BadgeIcon = badge.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999999] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-6xl h-[92vh] flex flex-col bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 bg-gray-900/90 border-b border-gray-800 backdrop-blur-md z-20">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 rounded-xl border flex items-center justify-center ${badge.color}`}>
                <BadgeIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold text-base sm:text-lg truncate max-w-xs sm:max-w-md md:max-w-lg">
                    {material.title}
                  </h3>
                  <span className="px-2 py-0.5 text-xs font-bold rounded bg-gray-800 text-gray-300 border border-gray-700">
                    {fileFormat}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate max-w-sm sm:max-w-md">
                  {material.course?.title || material.course?.code || fileName}
                </p>
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-2">
              {/* External open */}
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open raw preview in new tab"
                className="p-2 text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors border border-gray-700/50"
              >
                <ExternalLink className="w-4 h-4" />
              </a>

              {/* Download */}
              <a
                href={downloadUrl}
                download={fileName}
                title="Download original file"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors shadow-lg shadow-blue-500/20"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download</span>
              </a>

              {/* Close */}
              <button
                onClick={onClose}
                title="Close (Esc)"
                className="p-2 text-gray-400 hover:text-white hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Modal Main Content Body */}
          <div className="relative flex-1 bg-gray-950 overflow-hidden flex flex-col">
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/80 backdrop-blur-sm z-30">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-3" />
                <p className="text-sm text-gray-300 font-medium">Loading preview...</p>
                <p className="text-xs text-gray-500 mt-1">Rendering {fileFormat} file</p>
              </div>
            )}

            {/* IMAGE PREVIEW */}
            {category === 'image' && (
              <div className="relative flex-1 flex flex-col items-center justify-center overflow-hidden p-4">
                {/* Floating controls */}
                <div className="absolute top-4 z-10 flex items-center gap-1.5 bg-gray-900/90 border border-gray-800 backdrop-blur-md rounded-xl p-1.5 shadow-lg">
                  <button
                    onClick={() => setImageScale(s => Math.min(s + 0.25, 3))}
                    title="Zoom In"
                    className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setImageScale(s => Math.max(s - 0.25, 0.5))}
                    title="Zoom Out"
                    className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setImageRotation(r => (r + 90) % 360)}
                    title="Rotate"
                    className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setImageScale(1); setImageRotation(0); }}
                    title="Reset"
                    className="p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition text-xs font-semibold"
                  >
                    Reset
                  </button>
                </div>

                <div className="w-full h-full flex items-center justify-center overflow-auto">
                  <img
                    src={previewUrl}
                    alt={material.title}
                    style={{
                      transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
                      transition: 'transform 0.2s ease-out'
                    }}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                    onError={() => setError('Failed to load image preview.')}
                  />
                </div>
              </div>
            )}

            {/* VIDEO PREVIEW */}
            {category === 'video' && (
              <div className="relative flex-1 flex flex-col items-center justify-center p-4 sm:p-6 bg-black">
                <div className="relative w-full max-w-4xl max-h-full aspect-video rounded-xl overflow-hidden bg-gray-900 border border-gray-800 shadow-2xl flex items-center justify-center">
                  <video
                    ref={videoRef}
                    src={previewUrl}
                    controls
                    playsInline
                    autoPlay
                    className="w-full h-full object-contain"
                    onError={() => setError('Browser was unable to play this video stream directly.')}
                  >
                    Your browser does not support HTML5 video preview.
                  </video>
                </div>
              </div>
            )}

            {/* AUDIO PREVIEW */}
            {category === 'audio' && (
              <div className="relative flex-1 flex items-center justify-center p-6 bg-gradient-to-b from-gray-900 to-gray-950">
                <div className="w-full max-w-lg bg-gray-900/90 border border-gray-800 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl flex flex-col items-center">
                  <div className="relative w-32 h-32 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-500 flex items-center justify-center shadow-xl shadow-purple-500/20 mb-6">
                    <Music className="w-16 h-16 text-white animate-pulse" />
                  </div>

                  <h4 className="text-white text-lg sm:text-xl font-bold text-center line-clamp-1 mb-1">
                    {material.title}
                  </h4>
                  <p className="text-gray-400 text-sm mb-6 text-center">
                    {material.course?.title || 'Audio Recording'}
                  </p>

                  <audio
                    ref={audioRef}
                    src={previewUrl}
                    onTimeUpdate={() => setAudioCurrentTime(audioRef.current?.currentTime || 0)}
                    onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration || 0)}
                    onEnded={() => setIsPlaying(false)}
                    onError={() => setError('Could not play audio track.')}
                  />

                  {/* Scrubber */}
                  <div className="w-full mb-4">
                    <input
                      type="range"
                      min={0}
                      max={audioDuration || 100}
                      value={audioCurrentTime}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setAudioCurrentTime(val);
                        if (audioRef.current) audioRef.current.currentTime = val;
                      }}
                      className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1 font-mono">
                      <span>{formatTime(audioCurrentTime)}</span>
                      <span>{formatTime(audioDuration)}</span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center justify-center gap-6 w-full">
                    <button
                      onClick={() => {
                        const speeds = [0.75, 1, 1.25, 1.5, 2];
                        const nextIdx = (speeds.indexOf(playbackSpeed) + 1) % speeds.length;
                        const nextSpeed = speeds[nextIdx];
                        setPlaybackSpeed(nextSpeed);
                        if (audioRef.current) audioRef.current.playbackRate = nextSpeed;
                      }}
                      className="px-2.5 py-1 text-xs font-semibold text-gray-300 hover:text-white bg-gray-800 rounded-lg transition"
                      title="Playback Speed"
                    >
                      {playbackSpeed}x
                    </button>

                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          if (isPlaying) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          } else {
                            audioRef.current.play();
                            setIsPlaying(true);
                          }
                        }
                      }}
                      className="p-4 bg-purple-600 hover:bg-purple-500 text-white rounded-full shadow-lg shadow-purple-500/30 transition transform hover:scale-105"
                    >
                      {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                    </button>

                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.muted = !isMuted;
                          setIsMuted(!isMuted);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-white rounded-lg transition"
                      title={isMuted ? 'Unmute' : 'Mute'}
                    >
                      {isMuted ? <VolumeX className="w-5 h-5 text-red-400" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PDF PREVIEW */}
            {category === 'pdf' && (
              <div className="relative flex-1 w-full h-full bg-gray-900">
                <iframe
                  src={`${previewUrl}#toolbar=1&navpanes=1`}
                  title={material.title}
                  className="w-full h-full border-none"
                  onError={() => setError('Browser blocked PDF preview. You can open it in a new tab or download.')}
                />
              </div>
            )}

            {/* DOCX PREVIEW */}
            {category === 'docx' && (
              <div className="relative flex-1 flex flex-col w-full h-full bg-gray-950 overflow-hidden">
                {(material.file?.format || '').toLowerCase() === 'doc' ? (
                  // Legacy .doc format preview fallback
                  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                    <FileText className="w-16 h-16 text-blue-400 mb-4" />
                    <h4 className="text-white text-lg font-semibold mb-2">Legacy Word Document (.doc)</h4>
                    <p className="text-gray-400 text-sm max-w-md mb-6">
                      Legacy binary Word (.doc) files are best viewed via Google Docs Viewer or downloaded to Microsoft Word.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <a
                        href={googleViewerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Preview in Google Docs Viewer
                      </a>
                      <a
                        href={downloadUrl}
                        download={fileName}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm font-medium rounded-xl transition flex items-center gap-2 border border-gray-700"
                      >
                        <Download className="w-4 h-4" />
                        Download File
                      </a>
                    </div>
                  </div>
                ) : (
                  // Modern .docx render in DOM
                  <div className="flex-1 overflow-auto p-4 sm:p-8 flex justify-center bg-gray-900/60">
                    <div
                      ref={docxContainerRef}
                      className="bg-white text-gray-900 rounded-lg shadow-2xl p-6 sm:p-12 max-w-4xl w-full min-h-full font-serif text-sm leading-relaxed docx-render-target"
                      style={{ minHeight: '800px' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* SPREADSHEET (XLSX, XLS, CSV) PREVIEW */}
            {category === 'spreadsheet' && (
              <div className="relative flex-1 flex flex-col w-full h-full bg-gray-950 overflow-hidden">
                {/* Sheet Tabs */}
                {sheetNames.length > 1 && (
                  <div className="flex items-center gap-1 px-4 py-2 bg-gray-900 border-b border-gray-800 overflow-x-auto">
                    <span className="text-xs font-semibold text-gray-400 mr-2 uppercase">Sheets:</span>
                    {sheetNames.map(name => (
                      <button
                        key={name}
                        onClick={() => handleSheetSwitch(name)}
                        className={`px-3 py-1 text-xs rounded-lg font-medium transition ${
                          activeSheet === name
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex-1 overflow-auto p-4 sm:p-6 bg-gray-900 flex justify-center">
                  <div 
                    className="spreadsheet-table-container bg-white text-gray-900 rounded-xl shadow-xl overflow-auto w-full p-4 text-xs font-sans"
                    dangerouslySetInnerHTML={{ __html: sheetHtml }}
                  />
                </div>
              </div>
            )}

            {/* PPT / PPTX PREVIEW */}
            {category === 'pptx' && (
              <div className="relative flex-1 flex flex-col w-full h-full bg-gray-950 overflow-hidden">
                {/* Viewer Selector Bar */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800 text-xs">
                  <div className="flex items-center gap-2 text-gray-400">
                    <span className="font-semibold text-amber-400">Presentation Viewer:</span>
                    <button
                      onClick={() => setOfficeViewerType('google')}
                      className={`px-2.5 py-1 rounded-md transition ${
                        officeViewerType === 'google'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : 'hover:text-white'
                      }`}
                    >
                      Google Docs Viewer
                    </button>
                    <button
                      onClick={() => setOfficeViewerType('office')}
                      className={`px-2.5 py-1 rounded-md transition ${
                        officeViewerType === 'office'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : 'hover:text-white'
                      }`}
                    >
                      Office Online Viewer
                    </button>
                  </div>

                  <a
                    href={downloadUrl}
                    download={fileName}
                    className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Slide Deck
                  </a>
                </div>

                <div className="relative flex-1 w-full h-full bg-gray-900">
                  <iframe
                    src={officeViewerType === 'google' ? googleViewerUrl : officeViewerUrl}
                    title={material.title}
                    className="w-full h-full border-none"
                    onError={() => setError('Online viewer could not connect to document stream. Download to view.')}
                  />

                  {/* Localhost help message */}
                  <div className="absolute bottom-3 left-4 right-4 sm:left-auto sm:right-4 max-w-sm bg-gray-900/90 border border-amber-500/30 rounded-xl p-3 text-xs text-gray-300 shadow-xl backdrop-blur-md">
                    <p className="font-semibold text-amber-400 mb-1 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Notice for local / private networks
                    </p>
                    <p className="text-gray-400 text-[11px] leading-tight">
                      Google and Office Online viewers require public domain URLs. If running on localhost, please click Download to present locally.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CODE & TEXT PREVIEW */}
            {category === 'code' && (
              <div className="relative flex-1 flex flex-col w-full h-full bg-gray-950 overflow-hidden">
                {/* Code Toolbar */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800 text-xs">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 font-mono">
                      {textContent ? `${textContent.split('\n').length} lines` : '0 lines'}
                    </span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-400 font-mono">
                      {textContent ? `${(new Blob([textContent]).size / 1024).toFixed(1)} KB` : '0 KB'}
                    </span>
                  </div>

                  <button
                    onClick={handleCopyText}
                    className="flex items-center gap-1.5 px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg transition font-medium border border-gray-700/50"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                  </button>
                </div>

                <div className="flex-1 overflow-auto p-4 sm:p-6 font-mono text-xs sm:text-sm text-gray-200 bg-[#0d1117] leading-relaxed">
                  <pre className="whitespace-pre overflow-x-auto">
                    <code>{textContent}</code>
                  </pre>
                </div>
              </div>
            )}

            {/* OTHER / GENERIC FALLBACK */}
            {category === 'other' && (
              <div className="relative flex-1 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center mb-4 text-gray-400">
                  <File className="w-10 h-10" />
                </div>
                <h4 className="text-white text-lg font-bold mb-2">{fileName}</h4>
                <p className="text-gray-400 text-sm max-w-md mb-6">
                  This file format ({fileFormat}) doesn't support interactive inline browser preview. Download it to view in your system viewer.
                </p>
                <a
                  href={downloadUrl}
                  download={fileName}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl transition shadow-lg shadow-blue-500/25"
                >
                  <Download className="w-4 h-4" />
                  Download File
                </a>
              </div>
            )}

            {/* Error Overlay */}
            {error && (
              <div className="absolute bottom-4 left-4 right-4 z-40 bg-red-950/90 border border-red-800 text-red-200 px-4 py-3 rounded-xl backdrop-blur-md shadow-2xl flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <span>{error}</span>
                </div>
                <a
                  href={downloadUrl}
                  download={fileName}
                  className="underline text-red-300 hover:text-white font-semibold ml-4 shrink-0"
                >
                  Download instead
                </a>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Helper presentation icon component
const PresentationIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

export default FilePreviewModal;
